import React, { useEffect, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import * as LoyaltyService from '~/services/LoyaltyService';

import ApiError from '~/types/ApiError';
import {
  CUSTOM_RULE_PURPOSE_OPTIONS,
  CUSTOM_RULE_TYPE_OPTIONS,
  type CustomRule,
  type CustomRulePurpose,
  type CustomRuleType,
  MAX_END_DATE,
  MIN_START_DATE,
  POINTS_TYPE_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  type PointsType,
  type ProductType,
} from '~/types/loyaltyContract';

interface Props {
  themeColor: string;
  open: boolean;
  onClose: (updatedRule?: CustomRule | null) => void;
  rule: CustomRule | null;
}

interface UpdateFormData {
  type: CustomRuleType;
  purpose: CustomRulePurpose;
  pointsType: PointsType;
  productType: ProductType | null;
  name: string;
  startDate: string;
  endDate: string | null;
  offerId?: string;
  packageId?: string;
  earnKey?: string;
  promoKey?: string;
  region?: string;
  pointsCap?: number;
  pointsValue?: number;
  pointsMultiplier?: number;
  pointsRatio?: number;
  pointsValidDays?: number;
  pointsValidMonths?: number;
  identifierDescription?: string;
}

export default function UpdateCustomRuleDialog({ themeColor, open, onClose, rule }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Identifier type selection
  type IdentifierType = 'offerId' | 'packageId' | 'earnKey' | 'promoKey' | 'productType';
  const [selectedIdentifierType, setSelectedIdentifierType] = useState<IdentifierType>('earnKey');

  const identifierOptions = [
    { value: 'offerId', label: 'Offer ID' },
    { value: 'packageId', label: 'Package ID' },
    { value: 'earnKey', label: 'Earn Key' },
    { value: 'promoKey', label: 'Promo Key' },
    { value: 'productType', label: 'Product Type' },
  ];

  const [formData, setFormData] = useState<UpdateFormData>({
    type: 'BONUS_POINTS_ONLY',
    purpose: 'ESTIMATION_AND_EARN',
    pointsType: 'BONUS_POINTS',
    productType: null,
    name: '',
    startDate: '',
    endDate: null,
  });

  // Check if rule has already started
  const hasRuleStarted = (rule: CustomRule | null): boolean => {
    if (!rule || !rule.startDate) return false;
    const now = dayjs();
    const startDate = dayjs(rule.startDate);
    return startDate.isBefore(now) || startDate.isSame(now, 'day');
  };

  const isRuleStarted = hasRuleStarted(rule);

  // Initialize form data
  useEffect(() => {
    if (rule) {
      let identifierType: IdentifierType = 'offerId';
      if (rule.offerId) identifierType = 'offerId';
      else if (rule.packageId) identifierType = 'packageId';
      else if (rule.earnKey) identifierType = 'earnKey';
      else if (rule.promoKey) identifierType = 'promoKey';
      else if (rule.productType) identifierType = 'productType';

      setSelectedIdentifierType(identifierType);

      setFormData({
        type: rule.type,
        purpose: rule.purpose,
        pointsType: rule.pointsType,
        productType: rule.productType,
        name: rule.name,
        startDate: rule.startDate ? dayjs(rule.startDate).format('YYYY-MM-DD') : '',
        // Subtract 1 day from endDate to show the actual "last valid day" instead of the "expiry timestamp"
        endDate: rule.endDate ? dayjs(rule.endDate).subtract(1, 'day').format('YYYY-MM-DD') : null,
        offerId: rule.offerId || '',
        packageId: rule.packageId || '',
        earnKey: rule.earnKey || '',
        promoKey: rule.promoKey || '',
        region: rule.region || '',
        pointsCap: rule.pointsCap,
        pointsValue: rule.pointsValue,
        pointsMultiplier: rule.pointsMultiplier,
        pointsRatio: rule.pointsRatio,
        pointsValidDays: rule.pointsValidDays,
        pointsValidMonths: rule.pointsValidMonths,
      });
    }
  }, [rule]);

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const updateMutation = useMutation({
    mutationFn: (updateData: { id: string; data: Parameters<typeof LoyaltyService.updateCustomRule>[1] }) =>
      LoyaltyService.updateCustomRule(updateData.id, updateData.data),
    onSuccess: (response) => {
      enqueueSnackbar('Custom rule updated successfully', { variant: 'success' });
      // Invalidate queries to mark them as stale - they'll refetch automatically
      queryClient.invalidateQueries({ queryKey: ['loyalty', 'custom-rules'] });
      setValidationErrors({});
      // Pass the updated rule back to the parent component
      const updatedRule = response?.result?.updatedRule || null;
      handleClose(updatedRule);
    },
    onError: (error: ApiError) => {
      // Handle 400 validation errors
      if (error.name === 'ValidationError' && error.status === 400 && error.errors) {
        const errors: Record<string, string> = {};

        // Parse validation errors - handle both string format (Zod) and object format (schema validation)
        if (Array.isArray(error.errors)) {
          error.errors.forEach((errorItem: string | { message: string; path: string; value: string | object }) => {
            if (typeof errorItem === 'string') {
              // Zod error format: "fieldName: error message"
              const match = errorItem.match(/^(\w+):\s*(.+)$/);
              if (match) {
                const [, field, message] = match;
                errors[field] = message;
              }
            } else if (typeof errorItem === 'object' && errorItem.path && errorItem.message) {
              // Schema validation error format: { message, path, value }
              // Extract field name from path like "rule.identifierDescription"
              const pathParts = errorItem.path.split('.');
              const field = pathParts[pathParts.length - 1]; // Get last part of path
              errors[field] = errorItem.message;
            }
          });
        }

        setValidationErrors(errors);
        enqueueSnackbar('Please fix validation errors', { variant: 'error' });
      } else {
        enqueueSnackbar(`Error updating custom rule: ${error.message || 'Unknown error'}`, { variant: 'error' });
      }
    },
  });

  const handleClose = (updatedRule?: CustomRule | null): void => {
    setFormData({
      type: 'BONUS_POINTS_ONLY',
      purpose: 'ESTIMATION_AND_EARN',
      pointsType: 'BONUS_POINTS',
      productType: null,
      name: '',
      startDate: '',
      endDate: null,
    });
    setValidationErrors({});
    onClose(updatedRule);
  };

  const handleSubmit = (): void => {
    if (!rule) {
      return;
    }

    // For rules that have started, we want to only update the end date and early return
    if (isRuleStarted) {
      if (!formData.endDate) {
        enqueueSnackbar('Please provide an end date', { variant: 'error' });
        return;
      }

      const minimalUpdateData = {
        type: formData.type,
        endDate: dayjs.tz(formData.endDate, 'Australia/Sydney').add(1, 'day').startOf('day').toISOString(),
      };

      updateMutation.mutate({ id: rule.id, data: minimalUpdateData });
      return;
    }

    // For rules that haven't started yet, allow full update
    if (!formData.name || !formData.startDate || !formData.type || !formData.purpose || !formData.pointsType) {
      enqueueSnackbar('Please fill in all required fields (Name, Type, Purpose, Points Type, Start Date)', {
        variant: 'error',
      });
      return;
    }

    const updateData = {
      type: formData.type,
      purpose: formData.purpose,
      pointsType: formData.pointsType,
      productType: selectedIdentifierType === 'productType' ? formData.productType || null : null,
      name: formData.name,
      // Convert to Sydney timezone midnight and then to ISO 8601 UTC
      startDate: dayjs.tz(formData.startDate, 'Australia/Sydney').startOf('day').toISOString(),
      endDate: formData.endDate
        ? dayjs.tz(formData.endDate, 'Australia/Sydney').add(1, 'day').startOf('day').toISOString()
        : null,
      // Identifier fields - only one should be set based on selectedIdentifierType
      offerId: selectedIdentifierType === 'offerId' ? formData.offerId || null : null,
      packageId: selectedIdentifierType === 'packageId' ? formData.packageId || null : null,
      earnKey: selectedIdentifierType === 'earnKey' ? formData.earnKey || null : null,
      promoKey: selectedIdentifierType === 'promoKey' ? formData.promoKey || null : null,
      region: null, // Hidden field, rarely used
      pointsCap: null, // Always null based on server rules
      pointsValue:
        formData.type === 'BONUS_POINTS_ONLY' || formData.type === 'BONUS_POINTS' ? formData.pointsValue ?? null : null,
      pointsMultiplier: formData.type === 'MULTIPLE_ON_BASE_POINTS' ? formData.pointsMultiplier ?? null : null,
      pointsRatio: null, // Always null based on server rules
      pointsValidDays: null, // Hidden field, rarely used
      pointsValidMonths: formData.type === 'BONUS_POINTS_ONLY' ? 6 : 36,
    };

    updateMutation.mutate({ id: rule.id, data: updateData });
  };

  if (!rule) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isRuleStarted ? 'Update End Date (Rule Already Started)' : 'Update Custom Rule'}</DialogTitle>
      <DialogContent>
        {isRuleStarted && (
          <Box mt={2} mb={2} p={2} sx={{ bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.contrastText">
              This rule has already started and cannot be fully edited. You can only update the end date.
            </Typography>
          </Box>
        )}
        <Box mt={2}>
          <Stack spacing={3}>
            {!isRuleStarted && (
              <>
                <TextField
                  label="Name"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });

                    if (validationErrors.name) {
                      setValidationErrors({ ...validationErrors, name: '' });
                    }
                  }}
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                  required
                  fullWidth
                />

                <Stack direction="row" spacing={2}>
                  <FormControl fullWidth required>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Type"
                      onChange={(e: SelectChangeEvent<CustomRuleType>) => {
                        const newType = e.target.value;
                        // Type guard to ensure we have a valid CustomRuleType
                        if (
                          newType === 'BONUS_POINTS_ONLY' ||
                          newType === 'MULTIPLE_ON_BASE_POINTS' ||
                          newType === 'BONUS_POINTS'
                        ) {
                          setFormData({
                            ...formData,
                            type: newType,
                            // Auto-set purpose based on type
                            purpose: newType === 'BONUS_POINTS_ONLY' ? 'ESTIMATION_AND_EARN' : 'ESTIMATION_ONLY',
                            pointsCap: null,
                            pointsValue:
                              newType === 'BONUS_POINTS_ONLY' || newType === 'BONUS_POINTS'
                                ? formData.pointsValue
                                : undefined,
                            pointsMultiplier:
                              newType === 'MULTIPLE_ON_BASE_POINTS' ? formData.pointsMultiplier : undefined,
                            pointsRatio: null,
                            // Reset pointsType to BONUS_POINTS (valid for all types) when type changes
                            pointsType: 'BONUS_POINTS',
                          });
                        }
                      }}
                    >
                      {CUSTOM_RULE_TYPE_OPTIONS.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth required>
                    <InputLabel>Purpose</InputLabel>
                    <Select
                      value={formData.purpose}
                      label="Purpose"
                      disabled
                      onChange={(e: SelectChangeEvent<CustomRulePurpose>) =>
                        setFormData({ ...formData, purpose: e.target.value as CustomRulePurpose })
                      }
                    >
                      {CUSTOM_RULE_PURPOSE_OPTIONS.map((purpose) => (
                        <MenuItem key={purpose} value={purpose}>
                          {purpose}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Stack direction="row" spacing={2}>
                  <FormControl fullWidth required>
                    <InputLabel>Points Type</InputLabel>
                    <Select
                      value={formData.pointsType}
                      label="Points Type"
                      onChange={(e: SelectChangeEvent<PointsType>) =>
                        setFormData({ ...formData, pointsType: e.target.value as PointsType })
                      }
                    >
                      {POINTS_TYPE_OPTIONS.filter((type) => {
                        // only allowed BONUS_POINTS, BONUS_STATUS_CREDITS for new rules
                        return type === 'BONUS_POINTS' || type === 'BONUS_STATUS_CREDITS';
                      }).map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Stack direction="row" spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Identifier Type</InputLabel>
                    <Select
                      value={selectedIdentifierType}
                      label="Identifier Type"
                      onChange={(e: SelectChangeEvent<IdentifierType>) => {
                        const newType = e.target.value;
                        if (
                          newType === 'offerId' ||
                          newType === 'packageId' ||
                          newType === 'earnKey' ||
                          newType === 'promoKey' ||
                          newType === 'productType'
                        ) {
                          setSelectedIdentifierType(newType);

                          setFormData({
                            ...formData,
                            offerId: undefined,
                            packageId: undefined,
                            earnKey: undefined,
                            promoKey: undefined,
                            productType: undefined,
                          });
                          const newErrors = { ...validationErrors };
                          delete newErrors.offerId;
                          delete newErrors.packageId;
                          delete newErrors.earnKey;
                          delete newErrors.promoKey;
                          delete newErrors.productType;
                          setValidationErrors(newErrors);
                        }
                      }}
                    >
                      {identifierOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedIdentifierType === 'productType' ? (
                    <FormControl fullWidth required error={!!validationErrors.productType}>
                      <InputLabel>Product Type</InputLabel>
                      <Select
                        value={formData.productType || ''}
                        label="Product Type"
                        onChange={(e: SelectChangeEvent<string>) => {
                          setFormData({ ...formData, productType: e.target.value as ProductType });

                          if (validationErrors.productType) {
                            setValidationErrors({ ...validationErrors, productType: '' });
                          }
                        }}
                      >
                        {PRODUCT_TYPE_OPTIONS.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.productType && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {validationErrors.productType}
                        </Typography>
                      )}
                    </FormControl>
                  ) : (
                    <TextField
                      label={identifierOptions.find((opt) => opt.value === selectedIdentifierType)?.label}
                      value={formData[selectedIdentifierType] || ''}
                      onChange={(e) => {
                        setFormData({ ...formData, [selectedIdentifierType]: e.target.value });
                        if (validationErrors[selectedIdentifierType]) {
                          setValidationErrors({ ...validationErrors, [selectedIdentifierType]: '' });
                        }
                      }}
                      error={!!validationErrors[selectedIdentifierType]}
                      helperText={validationErrors[selectedIdentifierType]}
                      required
                      fullWidth
                    />
                  )}
                </Stack>

                <Stack direction="row" spacing={2}>
                  {(formData.type === 'BONUS_POINTS_ONLY' || formData.type === 'BONUS_POINTS') && (
                    <TextField
                      label="Points Value"
                      type="number"
                      value={formData.pointsValue ?? ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setFormData({
                          ...formData,
                          pointsValue: value === 0 ? undefined : value,
                        });

                        if (validationErrors.pointsValue) {
                          setValidationErrors({ ...validationErrors, pointsValue: '' });
                        }
                      }}
                      error={!!validationErrors.pointsValue}
                      helperText={validationErrors.pointsValue}
                      required
                      fullWidth
                    />
                  )}
                  {formData.type === 'MULTIPLE_ON_BASE_POINTS' && (
                    <TextField
                      label="Points Multiplier"
                      type="number"
                      value={formData.pointsMultiplier ?? ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setFormData({
                          ...formData,
                          pointsMultiplier: value === 0 ? undefined : value,
                        });

                        if (validationErrors.pointsMultiplier) {
                          setValidationErrors({ ...validationErrors, pointsMultiplier: '' });
                        }
                      }}
                      error={!!validationErrors.pointsMultiplier}
                      helperText={validationErrors.pointsMultiplier}
                      required
                      fullWidth
                    />
                  )}
                </Stack>

                <TextField
                  label="Points Valid Months"
                  type="number"
                  value={formData.type === 'BONUS_POINTS_ONLY' ? 6 : 36}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setFormData({
                      ...formData,
                      pointsValidMonths: value === 0 ? undefined : value,
                    });

                    if (validationErrors.pointsValidMonths) {
                      setValidationErrors({ ...validationErrors, pointsValidMonths: '' });
                    }
                  }}
                  disabled
                  error={!!validationErrors.pointsValidMonths}
                  helperText={
                    formData.type === 'BONUS_POINTS_ONLY'
                      ? 'Fixed at 6 months for Bonus Points Only type'
                      : 'Fixed at 36 months for this type'
                  }
                  fullWidth
                />

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => {
                      setFormData({ ...formData, startDate: e.target.value });

                      if (validationErrors.startDate) {
                        setValidationErrors({ ...validationErrors, startDate: '' });
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    slotProps={{
                      htmlInput: {
                        min: MIN_START_DATE,
                        max: MAX_END_DATE,
                      },
                    }}
                    error={!!validationErrors.startDate}
                    helperText={validationErrors.startDate || `Must be on or after today's date`}
                    required
                    fullWidth
                  />
                </Stack>
              </>
            )}

            <TextField
              label="End Date"
              type="date"
              value={formData.endDate || ''}
              onChange={(e) => {
                setFormData({ ...formData, endDate: e.target.value || null });

                if (validationErrors.endDate) {
                  setValidationErrors({ ...validationErrors, endDate: '' });
                }
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: formData.startDate || MIN_START_DATE,
                max: MAX_END_DATE,
              }}
              error={!!validationErrors.endDate}
              helperText={
                validationErrors.endDate ||
                (isRuleStarted
                  ? 'Required - Rule will be valid through the end of this date (inclusive)'
                  : `Optional - Rule will be valid through the end of this date (inclusive)`)
              }
              required={isRuleStarted}
              fullWidth
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose()} variant="text" sx={{ color: themeColor }}>
          Cancel
        </Button>
        <Button
          sx={{ backgroundColor: themeColor }}
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
